/**
 * Account product logic for the procurement-focused Clumse app.
 */

import {
  TierKey,
  userHasPermission,
  resolveUserTier,
  Permission,
} from "../lib/pricing";

// Simplified user tier resolution
export function getUserTier(userInfo) {
  return resolveUserTier(userInfo);
}

// Check if user has access to procurement features
export function hasProcurementAccess(userInfo) {
  return userHasPermission(userInfo, Permission.VENDOR_SCOUTING);
}

export function hasNegotiationAccess(userInfo) {
  return userHasPermission(userInfo, Permission.AI_NEGOTIATION);
}

export function hasEscrowAccess(userInfo) {
  return false; // Escrow functionality removed
}

export function hasSavingsTracking(userInfo) {
  return userHasPermission(userInfo, Permission.SAVINGS_TRACKING);
}

// Default dashboard route for all users (procurement-focused)
export function getPostLoginHomePath(_userInfo) {
  return "/chat";
}

// Simplified subscription status checking
export function hasActiveSubscription(userInfo) {
  if (!userInfo) return false;
  
  const tier = resolveUserTier(userInfo);
  
  // Retail tier doesn't require subscription (pay-per-job)
  if (tier === TierKey.INDIVIDUAL) return true;
  
  // Other tiers require active subscription
  const status = userInfo.subscriptionStatus;
  return status === "active" || status === "trialing";
}

// Check if user is on retail (pay-per-job) tier
export function isRetailTier(userInfo) {
  return resolveUserTier(userInfo) === TierKey.INDIVIDUAL;
}

// Check if user needs to upgrade for more concurrent jobs
export function needsUpgradeForConcurrency(userInfo, currentJobs) {
  const tier = resolveUserTier(userInfo);
  
  if (tier === TierKey.INDIVIDUAL && currentJobs >= 1) return true;
  if (tier === TierKey.PROFESSIONAL && currentJobs >= 5) return true;
  if (tier === TierKey.INSTITUTIONAL && currentJobs >= 25) return true;
  
  return false;
}

// Get upgrade path for user
export function getUpgradePath(userInfo) {
  const tier = resolveUserTier(userInfo);
  
  switch (tier) {
    case TierKey.INDIVIDUAL:
      return "/upgrade?to=professional";
    case TierKey.PROFESSIONAL:
      return "/upgrade?to=institutional";
    case TierKey.INSTITUTIONAL:
      return "/contact?for=enterprise";
    default:
      return "/pricing";
  }
}

// Legacy compatibility functions (simplified)
export function isFreeTierUser(userInfo) {
  return resolveUserTier(userInfo) === TierKey.INDIVIDUAL;
}

// Removed complex features:
// - Unit/door caps and tracking
// - Multi-tier permission matrices
// - Legacy subscription migration logic
// - Grandfathering rules
// - Rent collection permissions
// - Leasing permissions
// - Tax prep permissions

// Legacy access control functions (stubs)
export const hasFullOperatingSystemAccess = () => true; // Full access in procurement app
export const shouldShowScaleUpgradeForFullOs = () => false; // No scale OS in procurement app